import { calculateWithholding, isWithholdingApplicable } from './withholding';

/**
 * Entrée minimale pour construire un certificat à partir d'une facture.
 * Les valeurs HT/TVA/TTC proviennent directement du document source.
 */
export interface BuildCertificateSourceInvoice {
  docDate: string;          // ISO date
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  vatRate: number;          // taux TVA global moyen (ou 0 si incohérent)
}

export interface BuildCertificateThirdParty {
  type: 'company' | 'individual';
}

export interface BuildCertificateSettings {
  withholdingRate: number;      // en %, ex 1
  withholdingThreshold: number; // en DT, ex 1000
}

export interface CertificateLineDraft {
  idTypeOperation: string;   // ex "RS1_000001"
  anneeFacturation: number;
  cnpc: number;
  pCharge: number;
  montantHt: number;
  tauxRs: number;
  tauxTva: number;
  montantTva: number;
  montantTtc: number;
  montantRs: number;
  montantNetServi: number;
}

export interface CertificateDraft {
  lines: CertificateLineDraft[];
  totalHt: number;
  totalVat: number;
  totalTtc: number;
  totalRas: number;
  totalNetServi: number;
}

/**
 * Construit un brouillon de certificat RAS à partir d'une facture unique.
 * Une ligne d'opération = l'intégralité de la facture (agrégée).
 *
 * IdTypeOperation est auto-séquentiel, format "RS{kind}_{seq}" sur 6 chiffres.
 */
export function buildCertificateFromInvoice(
  invoice: BuildCertificateSourceInvoice,
  thirdParty: BuildCertificateThirdParty,
  settings: BuildCertificateSettings,
  opSequence: number,
  opKind = 1,
): CertificateDraft {
  const applicable = isWithholdingApplicable(
    thirdParty.type,
    invoice.totalTtc,
    settings.withholdingThreshold,
  );

  const tauxRs = applicable ? settings.withholdingRate : 0;
  const montantRs = applicable ? calculateWithholding(invoice.totalTtc, tauxRs) : 0;
  const montantNetServi = round3(invoice.totalTtc - montantRs);

  const line: CertificateLineDraft = {
    idTypeOperation: formatIdTypeOperation(opKind, opSequence),
    anneeFacturation: parseYear(invoice.docDate),
    cnpc: 0,
    pCharge: 0,
    montantHt: round3(invoice.totalHt),
    tauxRs,
    tauxTva: invoice.vatRate,
    montantTva: round3(invoice.totalVat),
    montantTtc: round3(invoice.totalTtc),
    montantRs: round3(montantRs),
    montantNetServi,
  };

  return {
    lines: [line],
    totalHt: line.montantHt,
    totalVat: line.montantTva,
    totalTtc: line.montantTtc,
    totalRas: line.montantRs,
    totalNetServi: line.montantNetServi,
  };
}

export function formatIdTypeOperation(kind: number, seq: number): string {
  return `RS${kind}_${String(seq).padStart(6, '0')}`;
}

function parseYear(isoDate: string): number {
  return parseInt(isoDate.slice(0, 4), 10);
}

function round3(n: number): number {
  return Math.round(n * 1000) / 1000;
}
